import React from 'react';
import { Table, Input, Button, Dropdown, Menu, Checkbox, Steps, Space, Flex, Tag, TableColumnProps, Modal, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import ReusableTable from '../../../../components/TableComponent';
import { MasterConfigs } from '../../../../interfaces/entity';
import { constants } from '../../../../constants/common';
import ProfileCard from '../../../../components/ContactInfo/ProfileCard';
import moment from 'moment';
import StatusBadge from '../../../Leaves/AttendanceComponents/history/StatusBadge';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import { align } from 'suneditor/src/plugins';
import { useSelector, useDispatch } from 'react-redux';
import { postData } from '../../../../actions/developer';

const NewJoineeAndExist = () => {
    const [currentStep, setCurrentStep] = React.useState(0);
    const [holdModalVisible, setHoldModalVisible] = React.useState(false);
    const [selectedEmployee, setSelectedEmployee] = React.useState<any>(null);
    const [holdReason, setHoldReason] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const params = useSelector(({ app }) => app?.params)
    const currentMonth = moment(params?.selectedMonth || moment().format('MMM YYYY'), 'MMM YYYY').format('MM-YYYY')
    const dispatch = useDispatch();

    const config: MasterConfigs = {
        api: () => '/run-payroll/new-joinees',
        queryParams: {
            month: currentMonth
        },
        tableProps: {
            rowKey: '_id',
            scroll: { y: 450 },
            expandable: {
                expandedRowRender: (record) => {
                    return <Table
                        className='w-full mt-2'
                        columns={getAttendanceColumns()}
                        dataSource={record.attendanceLogs.map((log) => ({
                            key: log._id,
                            ...log,
                        }))}
                        scroll={{ y: 300 }}
                        pagination={{
                            pageSize: 7
                        }}
                        rowClassName={(record) =>
                            record.dayStatus === "absent" || record.totalHours < 1 && record.dayStatus == 'present'
                                ? "bg-red-50"
                                : ""
                        }
                    />
                },
                rowExpandable: (record) => true,
            }
        },
        getColumns: () => employeeColumns as TableColumnProps[],
    }

    const existConfig: MasterConfigs = {
        api: () => '/run-payroll/exist-employee',
        queryParams: {
            month: currentMonth
        },
        tableProps: {
            rowKey: '_id',
            scroll: { y: 450 },
            expandable: {
                expandedRowRender: (record) => {
                    return <Table
                        className='w-full mt-2'
                        columns={getAttendanceColumns()}
                        dataSource={record.attendanceLogs.map((log) => ({
                            key: log._id,
                            ...log,
                        }))}
                        scroll={{ y: 300 }}
                        pagination={{
                            pageSize: 7
                        }}
                        rowClassName={(record) =>
                            record.dayStatus === "absent" || record.totalHours < 1 && record.dayStatus == 'present'
                                ? "bg-red-50"
                                : ""
                        }
                    />
                },
                rowExpandable: (record) => true,
            }
        },
        getColumns: () => employeeColumns as TableColumnProps[],
    }

    const getAttendanceColumns = (): TableColumnProps[] => [
        {
            title: "Date",
            dataIndex: "date",
            width: 200,
            fixed: 'left',
            render: (date) => moment(date).format("dddd DD MMM, YYYY"),
        },
        {
            title: 'Clock In',
            dataIndex: 'startDate',
            key: 'startDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchStatus == 'on-time') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.startDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + (record?.punchStatus == 'on-time' ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchStatus}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: 'Clock Out',
            dataIndex: 'endDate',
            key: 'endDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchOut == 'on-time' || record.punchOut == 'late') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.endDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + ((record?.punchOut == 'on-time' || record?.punchOut == 'late') ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchOut}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: "Working Hours",
            dataIndex: "totalHours",
            width: 150,
            align: 'center',
            render: (hours, record) => record?.dayStatus == 'present' ? <>
                {
                    hours <= 5 ? (
                        <span className='text-red-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) : hours > 5 && hours <= 7 ? (
                        <span className='text-red-800 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) :
                        <span className='text-green-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                }
            </> : '-',
        },
        {
            title: "Day Status",
            dataIndex: "dayStatus",
            width: 150,
            align: 'center',
            render: (status) => {
                const colorMap: Record<string, string> = {
                    present: "green",
                    absent: "red",
                    weekend: "blue",
                    leave: "orange",
                };
                return <Tag bordered={false} color={colorMap[status] || "default"}>{status.toUpperCase()}</Tag>;
            },
        },
        {
            title: "Leave Type",
            width: 150,
            render: (_, record) =>
                record.leaveData?.leaveDetail?.leaveTypeDetail?.leaveName || "-",
        },
        {
            title: "Leave Status",
            width: 150,
            align: 'center',
            render: (_, record) =>
                record.leaveData?.leaveStatus ? (
                    <Tag color="green">{record.leaveData.leaveStatus}</Tag>
                ) : (
                    "-"
                ),
        },
    ];

    const handleHoldSalary = (record: any) => {
        setSelectedEmployee(record);
        setHoldModalVisible(true);
    };

    const handleConfirmHold = async () => {
        if (!holdReason.trim()) {
            message.error('Please provide a reason for holding salary');
            return;
        }

        setLoading(true);
        try {
            const response = await dispatch(postData('/run-payroll/hold-salary', {
                employeeId: selectedEmployee._id,
                month: currentMonth,
                reason: holdReason,
                holdType: 'salaryProcessingHold'
            }));

            if (response?.code === 200) {
                message.success('Salary held successfully');
                setHoldModalVisible(false);
                setHoldReason('');
                setSelectedEmployee(null);
                // Refresh table data
                setRefreshKey(prev => prev + 1);
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true,
                });
            } else {
                message.error(response?.message || 'Failed to hold salary');
            }
        } catch (error) {
            message.error('Error holding salary');
        } finally {
            setLoading(false);
        }
    };

    const getStatusInfo = (record: any) => {
        const salaryProcessing = record?.salaryProcessing;
        if (!salaryProcessing) {
            return { label: 'Active', color: 'green' };
        }
        if (salaryProcessing.voidSalaryProcessing) {
            return { label: 'Void Salary Processing', color: 'red' };
        }
        if (salaryProcessing.salaryPayoutHold) {
            return { label: 'Salary Payout on Hold', color: 'orange' };
        }
        if (salaryProcessing.salaryProcessingHold) {
            return { label: 'Salary Processing Hold', color: 'blue' };
        }
        return { label: 'Active', color: 'green' };
    };

    const employeeColumns = [
        {
            title: "Employee",
            align: 'left',
            render: (_, record) => <SmallProfileCard
                name={constants.GET_FULL_NAME(record)}
                avatar={record?.avatar}
                subTitle={record?.employeeId}
            />
        },
        {
            title: "Shift",
            align: 'center',
            dataIndex: "shiftName",
        },
        {
            title: "Working Days",
            align: 'center',
            dataIndex: "workingDays",
        },
        {
            title: "Present Days",
            align: 'center',
            dataIndex: "presentDays",
        },
        {
            title: "Absent Days",
            align: 'center',
            dataIndex: "absentDays",
        },
        {
            title: "Total Leaves",
            align: 'center',
            dataIndex: "totalLeaves",
        },
        {
            title: "Unpaid Leaves",
            align: 'center',
            dataIndex: "unpaidLeaves",
        },
        {
            title: "Status",
            align: 'center',
            width: 200,
            render: (_, record) => {
                const statusInfo = getStatusInfo(record);
                return (
                    <Tag color={statusInfo.color} style={{ fontSize: '12px', padding: '4px 12px' }}>
                        {statusInfo.label}
                    </Tag>
                );
            }
        },
        {
            title: "Action",
            align: 'center',
            width: 150,
            render: (_, record) => {
                const salaryProcessing = record?.salaryProcessing;
                const isOnHold = salaryProcessing?.salaryProcessingHold || salaryProcessing?.salaryPayoutHold || salaryProcessing?.voidSalaryProcessing;

                return (
                    <Button
                        type={isOnHold ? "default" : "default"}
                        size="small"
                        onClick={() => handleHoldSalary(record)}
                        disabled={isOnHold}
                    >
                        {isOnHold ? 'On Hold' : 'Hold Salary'}
                    </Button>
                );
            },
        },
    ];

    const configs = {
        0: config,
        1: existConfig,
        2: config,
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
            <div style={{ flex: '0 0 auto' }} className='bg-white shadow-md p-4  w-full'>
                <Flex align='center' gap={16}>
                    <div className='w-3/4 flex items-center gap-4'>
                        <Steps
                            current={currentStep}
                            size='small'
                            onChange={(e) => setCurrentStep(e)}
                            items={[
                                { title: 'New Joinees' },
                                { title: 'Employees in Exist process' },
                                { title: 'Full & Final Settelment' },
                            ]}
                        />
                    </div>
                    <div className='w-1/4 flex justify-end'>
                        <Button type="primary">Save & Continue</Button>
                    </div>
                </Flex>
            </div>

            <div
                style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    padding: '1.5rem',
                    backgroundColor: '#f9fafb' // same as bg-gray-50
                }}
            >
                <div className="bg-purple-100 text-purple-800 p-4 rounded mb-4 text-sm font-medium">
                    All leave (approved or pending) that falls under this payroll cycle month will be displayed here.
                </div>
                {currentStep >= 0 && <ReusableTable config={configs[currentStep]} key={`${currentStep}-${refreshKey}`} />}
            </div>

            <Modal
                title="Hold Salary"
                open={holdModalVisible}
                onOk={handleConfirmHold}
                onCancel={() => {
                    setHoldModalVisible(false);
                    setHoldReason('');
                    setSelectedEmployee(null);
                }}
                confirmLoading={loading}
            >
                <div style={{ marginBottom: '16px' }}>
                    <strong>Employee:</strong> {selectedEmployee && constants.GET_FULL_NAME(selectedEmployee)}
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <strong>Month:</strong> {moment(currentMonth, 'MM-YYYY').format('MMMM YYYY')}
                </div>
                <div>
                    <strong>Reason for Hold:</strong>
                    <Input.TextArea
                        rows={4}
                        value={holdReason}
                        onChange={(e) => setHoldReason(e.target.value)}
                        placeholder="Enter reason for holding salary..."
                        style={{ marginTop: '8px' }}
                    />
                </div>
            </Modal>
        </div>

    );
};

export default NewJoineeAndExist;
